/* eslint-disable */
import { useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { STORAGE, findCustomFolder, t } from "@/lib/mr";

/**
 * A spectator-created (custom) folder is purely decorative — it never
 * holds real notes and its only job is to look convincingly empty
 * inside the Folders → :id navigation flow. This page renders exactly
 * that: an iOS-native empty Notes list with the folder's name up top.
 *
 * If the URL param references a folder that no longer exists (e.g.
 * user cleared localStorage between sessions), we fall back to the
 * Folders overview so the spectator never sees a 404.
 */
export default function CustomFolder() {
  const navigate = useNavigate();
  const params = useParams();
  const langCode = localStorage.getItem(STORAGE.LANGUAGE) || "auto";
  const isRTL = /^(?:ar|he|fa|ur)/.test(langCode || "");

  const folder = findCustomFolder(params.id);

  // Missing folder → bounce back before any paint. useEffect keeps the
  // navigate() call out of render (avoiding React's "no navigation
  // during render" warning).
  useEffect(() => {
    if (!folder) navigate("/folders", { replace: true });
  }, [folder, navigate]);

  // Triple-tap escape hatch on the top 60 px strip — parity with the
  // rest of the disguise pages.
  const escTaps = useRef({ list: [] });
  useEffect(() => {
    const handler = (e) => {
      const y = e.touches?.[0]?.clientY ?? e.clientY;
      if (typeof y !== "number" || y > 60) return;
      const now = Date.now();
      escTaps.current.list = [...escTaps.current.list, now].filter((t) => now - t < 700);
      if (escTaps.current.list.length >= 3) {
        escTaps.current.list = [];
        navigate("/");
      }
    };
    window.addEventListener("touchstart", handler, { passive: true });
    window.addEventListener("mousedown", handler);
    return () => {
      window.removeEventListener("touchstart", handler);
      window.removeEventListener("mousedown", handler);
    };
  }, [navigate]);

  if (!folder) return null;

  return (
    <div
      className="fixed inset-0 w-screen h-screen bg-[#F2F2F7] text-black flex flex-col"
      style={{
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "SF Pro Text", "SF Pro Display", "Helvetica Neue", Arial, sans-serif',
        paddingTop: "env(safe-area-inset-top)",
        paddingBottom: "env(safe-area-inset-bottom)",
      }}
      dir={isRTL ? "rtl" : "ltr"}
      onContextMenu={(e) => e.preventDefault()}
      data-testid="custom-folder-page"
    >
      {/* Top bar */}
      <div className="px-4 pt-2 pb-1 flex items-center justify-between select-none">
        <button
          type="button"
          onClick={() => navigate("/folders")}
          className="flex items-center gap-1 text-[#FFB400] text-[17px] leading-none bg-transparent border-0 p-0"
          data-testid="custom-folder-back"
        >
          <svg width="12" height="20" viewBox="0 0 12 20" fill="none" aria-hidden="true">
            <path d="M10 1L2 10l8 9" stroke="#FFB400" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <span className="text-[17px] font-normal">{t("folders", langCode)}</span>
        </button>
        <span className="w-6" aria-hidden="true" />
      </div>

      {/* Big page title = folder name */}
      <div className="px-5 pt-3 pb-3 shrink-0">
        <h1
          className="text-[34px] font-bold leading-tight text-black truncate"
          data-testid="custom-folder-title"
        >
          {folder.name}
        </h1>
      </div>

      {/* Search bar — decorative iOS parity */}
      <div className="px-4 pb-3 shrink-0">
        <div className="flex items-center gap-2 bg-neutral-200/80 rounded-[10px] px-3 py-2">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <circle cx="11" cy="11" r="7" stroke="#8e8e93" strokeWidth="2" />
            <path d="M20 20l-3.2-3.2" stroke="#8e8e93" strokeWidth="2" strokeLinecap="round" />
          </svg>
          <input
            type="text"
            placeholder={t("search", langCode)}
            aria-label={t("search", langCode)}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="none"
            spellCheck="false"
            className="flex-1 bg-transparent border-0 outline-none text-[15px] text-black placeholder:text-neutral-500 leading-none p-0 min-w-0"
          />
        </div>
      </div>

      {/* Empty state */}
      <div className="flex-1 flex items-center justify-center pb-24" data-testid="custom-folder-empty-state">
        <span className="text-[17px] text-neutral-400">{t("no_notes", langCode)}</span>
      </div>
    </div>
  );
}
